import { useEffect, type ReactNode } from "react"

import { EmbedLinkStateEnum } from "@boundless/bl-api-client/common/models/EmbedLinkStateEnum"
import { useCommonEmbedLinkRetrieve } from "@boundless/bl-api-client/common/services"
import { QueryClientProvider } from "@tanstack/react-query"
import { RouterProvider } from "@tanstack/react-router"
import { useStore } from "zustand"

import { GenericErrorComponent } from "@/components/error/GenericErrorComponent"
import { PendingComponent } from "@/components/error/PendingComponent"
import { AuthWrapper } from "@/components/router/AuthWrapper"
import { CompanyWrapper } from "@/components/router/CompanyWrapper"
import { EmbedThemeWrapper } from "@/components/router/EmbedThemeWrapper"
import { PermissionWrapper } from "@/components/router/PermissionWrapper"
import { PortalContainerProvider } from "@/components/ui/portal-container"
import {
  EmbedTurnstileFrameContext,
  type EmbedTurnstileFrameConfig,
} from "@/embed/turnstile-frame-context"
import { isNotFoundError } from "@/lib/clients"
import { initializeMixpanel } from "@/lib/mixpanel-client"
import { store } from "@/lib/store"
import { queryClient, router } from "@/main"

export type UserConfig = {
  partnerId: string
  userId: string
  email: string | null
  firstName: string | null
  lastName: string | null
}

type BoundlessEmbedWrapperProps = UserConfig & {
  portalContainer?: HTMLElement | null
  turnstileFrame: EmbedTurnstileFrameConfig
}

type EmbedRouterGateProps = {
  children: ReactNode
  isAuthenticated: boolean
}

export function BoundlessEmbedWrapper({
  partnerId,
  userId,
  email,
  firstName,
  lastName,
  portalContainer = null,
  turnstileFrame,
}: BoundlessEmbedWrapperProps) {
  const isAuthenticated = useStore(store, (state) => state.isAuthenticated)

  useEffect(() => {
    initializeMixpanel()
  }, [])

  useEffect(() => {
    store.setState({
      embedPartnerId: partnerId,
      embedUserId: userId,
      embedPrefillEmail: email,
      embedPrefillFirstName: firstName,
      embedPrefillLastName: lastName,
    })

    return () => {
      store.setState({
        embedPartnerId: null,
        embedUserId: null,
        embedPrefillEmail: null,
        embedPrefillFirstName: null,
        embedPrefillLastName: null,
      })
    }
  }, [partnerId, userId, email, firstName, lastName])

  return (
    <EmbedTurnstileFrameContext value={turnstileFrame}>
      <PortalContainerProvider container={portalContainer}>
        <QueryClientProvider client={queryClient}>
          <EmbedThemeWrapper
            partnerId={partnerId}
            key={isAuthenticated ? "auth" : "unauth"}
          >
            <AuthWrapper>
              <PermissionWrapper>
                <EmbedRouterGate isAuthenticated={isAuthenticated}>
                  <CompanyWrapper>
                    <RouterProvider router={router} />
                  </CompanyWrapper>
                </EmbedRouterGate>
              </PermissionWrapper>
            </AuthWrapper>
          </EmbedThemeWrapper>
        </QueryClientProvider>
      </PortalContainerProvider>
    </EmbedTurnstileFrameContext>
  )
}

function EmbedRouterGate({ children, isAuthenticated }: EmbedRouterGateProps) {
  // Interval (ms) used while polling the backend for embed link state updates.
  // Overridable so the E2E harness can poll faster than the production default.
  const INTERVAL_MS = Number(import.meta.env.VITE_EMBED_LINK_POLL_MS) || 5000

  // Polls the embed link state until it transitions into a `fulfilled` state.
  // Polling continues only while the backend returns a non-fulfilled or error state.
  // Once state is `fulfilled` or `error`, polling stops automatically.
  const { data, isLoading, error } = useCommonEmbedLinkRetrieve({
    query: {
      enabled: isAuthenticated,
      refetchInterval: (query) => {
        // Stop polling if the request itself failed.
        if (query.state.status === "error") {
          return false
        }

        const state = query.state.data?.state

        // Stop polling once the embed flow has reached a `fulfilled`/`error` state.
        if (
          state === EmbedLinkStateEnum.fulfilled ||
          state === EmbedLinkStateEnum.error
        ) {
          return false
        }

        // Continue polling during `pending` or `notified` states.
        return INTERVAL_MS
      },
    },
  })

  if (!isAuthenticated) {
    return <>{children}</>
  }

  // While the embed link is still `pending` or `notified`, show a loading state.
  if (
    isLoading ||
    data?.state === EmbedLinkStateEnum.pending ||
    data?.state === EmbedLinkStateEnum.notified
  ) {
    return <PendingComponent />
  }

  if (error) {
    // Accounts created in the main application have no EmbedLink and therefore
    // no partner provisioning state to wait for.
    if (isNotFoundError(error)) {
      return <>{children}</>
    }

    return <GenericErrorComponent error={error} />
  }

  return <>{children}</>
}
